import {Suspense, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useVehicleInspectionReservationUtil from '@di-shared/features/public-service/vehicle-inspection/reservation/useVehicleInspectionReservationUtil';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface VehicleInspectionPaymentReservationProps {
  puid: string;
  vin: string;
  reservationNumber: string;
}

function VehicleInspectionPaymentReservation(props: VehicleInspectionPaymentReservationProps): JSX.Element {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {openAlert} = useCcWebviewUtilsHelper();
  return (
    <ErrorBoundary
      fallback={<Loading />}
      onError={async (error: any) => {
        await openAlert({
          title: t('T40169'),
          message: `error [${error.resultCode || 9999}-${props.reservationNumber}]`,
        });
        navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(props.vin), {replace: true});
      }}
    >
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({puid, vin, reservationNumber}: VehicleInspectionPaymentReservationProps) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {inspectionLabels} = useVehicleInspectionReservationUtil();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data: basicInfo} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getReservationStatus), puid, reservationNumber],
    queryFn: () => diApi.publicServiceInspection.getReservationStatus({puid, vin}),
    select(data) {
      if (!data.data || !data.data.statusList || data.data.statusList.length === 0) {
        throw new ExpectedError('Reservation not found');
      }
      const item = data.data.statusList.find((item) => item.reservationNumber === reservationNumber);
      if (!item) {
        throw new ExpectedError('Reservation not found');
      }
      return item;
    },
    refetchOnWindowFocus: true, // 수정 후 돌아올수도 있는 페이지라, 올떄마다 재로드
  });
  const [isWaitingPayment, setIsWaitingPayment] = useState(false);
  const [merchantTransactionNo, setMerchantTransactionNo] = useState<string | null>(null);

  const [payment] = usePendingHandler(
    async () => {
      WebviewUtils.loading(true);
      const {data} = await diApi.publicServiceInspection.reservePayment({
        requestBody: {
          puid,
          reservationNumber,
          redirectUrl: window.location.origin + ROUTE_PATHS.HMG_PAY_PAYMENT_CALLBACK.getFullPath(),
        },
      });
      WebviewUtils.loading(false);

      if (data.status === 'CONFIRMED') {
        // 의인인 경우, 결제 금액이 0원이면 바로 결제 승인처리
        await openAlert({
          title: t('T40231'),
        });
        WebviewUtils.close();
        return;
      }
      if (data.status === 'RESERVED' && data.paymentUrl) {
        // 결제 준비 상태, 결제 대기!
        setIsWaitingPayment(true);
        setMerchantTransactionNo(data.merchantTransactionNo); // 거래 조회에 사용될 거래 고유값. 반드시 존재한다
        WebviewUtils.openNewWebView({
          url: data.paymentUrl,
          isModal: true,
          isNavigationBar: false,
          isToolBar: false,
        });
        return;
      }

      // 그 외 결제 상태는 에러로 취급
      await openAlert({
        title: t('T40232'),
        message: `error [${data.status}-P1]`,
        okText: t('T40216'),
      });
    },
    async (error: any) => {
      WebviewUtils.loading(false);
      await openAlert({
        title: t('T40232'),
        message: `error [${error.resultCode || 9999}-P1]`,
        okText: t('T40216'),
      });
    }
  );

  if (isWaitingPayment) {
    return (
      <Payment
        puid={puid}
        merchantTransactionNo={merchantTransactionNo!}
        onCancelPayment={() => {
          setIsWaitingPayment(false);
          setMerchantTransactionNo(null);
        }}
      />
    );
  }

  const reservationDate = parseDateWithFormat(basicInfo.reservationDateTime);
  return (
    <>
      <main className="content">
        <section className="section section_reservation">
          <div className="section_container_header">
            <h2 className="title_xxsmall">{t('T40233')}</h2>
          </div>
          <dl className="reservation_box">
            <div className="reservation_info">
              <dt className="title">{t('T40190')}</dt>
              <dd className="info">{inspectionLabels[basicInfo.inspectionCategory] || '-'}</dd>
            </div>
            <div className="reservation_info">
              <dt className="title">{t('T40191')}</dt>
              <dd className="info">{reservationDate ? CldrDatetimeFormatter.format(reservationDate, 'yMMMaHM') : '-'}</dd>
            </div>
            <div className="reservation_info">
              <dt className="title">{t('T40192')}</dt>
              <dd className="info">{basicInfo.licensePlateNumber}</dd>
            </div>
            <div className="reservation_info">
              <dt className="title">{t('T40225')}</dt>
              <dd className="info type_bold">{t('T20384_$amount', {amount: formatNumberWithCommas(basicInfo.paymentAmount)})}</dd>
            </div>
          </dl>
        </section>
        <div className="divider divider_thick"></div>
        <section className="section section_payment">
          <div className="payment_area">
            <div className="section_container_header">
              <h2 className="title_xxsmall">{t('T40234')}</h2>
            </div>
            <button
              type="button"
              className="btn_payment"
              disabled
            >
              <span className="ic ic_logo_hyundaipay"></span>
              <span className="text">{t('T40235')}</span>
            </button>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => payment()}
        >
          {t('T30537')}
        </button>
      </div>
    </>
  );
}

const POLLING_DURATION = 2000;

function Payment({puid, merchantTransactionNo, onCancelPayment}: {puid: string; merchantTransactionNo: string; onCancelPayment(): void}) {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();

  useEffect(() => {
    let id: ReturnType<typeof requestAnimationFrame> | null = null;
    let time = Date.now();

    async function checkPayment() {
      const now = Date.now();
      if (now - time > POLLING_DURATION) {
        time = now;

        try {
          const {data} = await diApi.publicServiceInspection.getPayment({puid, merchantTransactionNo});
          if (data.payment.status === 'RESERVED') {
            // ********** 사용자가 아직도 결제중임. 다시 폴링
          } else if (data.payment.status === 'CONFIRMED') {
            // ********** 결제 성공 케이스
            await openAlert({
              title: t('T40231'),
            });
            WebviewUtils.close();
            return;
          } else if (data.payment.status === 'REFUNDED') {
            // ********** 환불 케이스는 정의된바 없으나 (플로우상 이럴순 없음) 방어해둠
            await openAlert({
              title: t('T40232'),
              message: `error [${data.payment.inspectionReservationId}-P4]`,
              okText: t('T40216'),
            });
            onCancelPayment();
            return;
          } else {
            // ********** 결제 실패 케이스
            if (data.payment.failureReasonCode === 'PUBLIC_SERVICE_API') {
              // 예약 확정 실페 케이스의 경우, 예약 선점 후 1시간이 지나 예약 선점이 취소된 경우로 예약 선점 전 컴포넌트로 복귀합니다.
              await openAlert({
                title: t('T40236'),
                message: `error [${data.payment.inspectionReservationId}-P5]`,
                okText: t('T40216'),
              });
              WebviewUtils.close();
              return;
            } else if (data.payment.failureReasonCode === 'USER_STOP') {
              // 사용자가 결제를 취소한 경우
              await openAlert({
                title: t('T40237'),
              });
              onCancelPayment();
              return;
            } else {
              // HMG PAY 결제 오류 케이스는 결제 초기 화면으로 돌아와 결제를 처음부터 다시 진행할 수 있습니다.
              await openAlert({
                title: t('T40232'),
                message: `error [${data.payment.inspectionReservationId}-P6]`,
                okText: t('T40216'),
              });
              onCancelPayment();
              return;
            }
          }
        } catch (error: any) {
          await openAlert({
            title: t('T40232'),
            message: `error [${error.resultCode || 9999}-P3]`,
            okText: t('T40216'),
          });
          onCancelPayment();
          return;
        }
      }
      // 오페라미니에서 안되는건 관심 없음
      /* eslint-disable */
      id = requestAnimationFrame(checkPayment);
    }

    /* eslint-disable */
    id = requestAnimationFrame(checkPayment);

    return () => {
      if (id) {
        cancelAnimationFrame(id);
      }
    };
  }, []);

  const portalRoot = document.getElementById('root');

  if (portalRoot) {
    return ReactDOM.createPortal(
      <div className="popup_wrap">
        <div className="popup loading_popup">
          <div className="popup_inner">
            <div className="popup_contents">
              <div className="loading_box">
                <div className="loading_lottie">
                  <LottieSelector
                    id="img_json_loading_black_60f"
                    kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                    genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                    loop={true}
                    autoplay={true}
                    style={{width: 25, height: 25}}
                  />
                </div>
                <span
                  className="loading_txt"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20411')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>,
      portalRoot
    );
  }
  return null;
}

function Loading() {
  const {t} = useInferableTranslation();
  return (
    <main className="content">
      <section className="section section_reservation">
        <div className="section_container_header">
          <h2 className="title_xxsmall">{t('T40233')}</h2>
        </div>
        <dl className="reservation_box">
          <div className="reservation_info skeleton">
            <dt className="title">&nbsp;</dt>
            <dd className="info">&nbsp;</dd>
          </div>
          <div className="reservation_info skeleton">
            <dt className="title">&nbsp;</dt>
            <dd className="info">&nbsp;</dd>
          </div>
          <div className="reservation_info skeleton">
            <dt className="title">&nbsp;</dt>
            <dd className="info">&nbsp;</dd>
          </div>
          <div className="reservation_info skeleton">
            <dt className="title">&nbsp;</dt>
            <dd className="info">&nbsp;</dd>
          </div>
        </dl>
      </section>
      <div className="divider divider_thick"></div>
      <section className="section section_payment">
        <div className="payment_area skeleton">
          <div className="section_container_header">
            <h2 className="title_xxsmall">&nbsp;</h2>
          </div>
          <button
            type="button"
            className="btn_payment"
          >
            <span className="ic ic_logo_hyundaipay"></span>
            <span className="text">{t('T40235')}</span>
          </button>
        </div>
      </section>
    </main>
  );
}

export default VehicleInspectionPaymentReservation;
